import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ThreeAvatar from "@/components/ThreeAvatar";
import { Crown, Trophy, UsersThree, CalendarCheck, Drone, Target } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && !u.startsWith("http") ? `${API_URL}${u}` : u);

/**
 * DashboardHero (v2) — pixel-matches the mobile mockup the user shared.
 *
 * Composition:
 *   - full-width background image chosen by admin (`/api/dashboard-theme`)
 *   - overlay cards: welcome + level progress + stats (partidos / amigos /
 *     próxima cita) + CTA "Tu próxima reta te espera" + Aerotomas tile
 *   - a mini 3D AR avatar rendered inside the welcome card using the signed-in
 *     user's `avatar3d` config + their uploaded `facePhoto` (same as Studio)
 */
export default function DashboardHero({ userName }) {
  const { user } = useAuth();
  const [theme, setTheme] = useState(null);
  const [stats, setStats] = useState({ matches: 0, friends: 0, next_date: null });
  const [nextMatch, setNextMatch] = useState(null);

  useEffect(() => {
    const reload = () => api.get("/dashboard-theme").then(({ data }) => setTheme(data?.theme || null)).catch(() => {});
    reload();
    const onVis = () => { if (document.visibilityState === "visible") reload(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("dashboard-theme:changed", reload);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("dashboard-theme:changed", reload);
    };
  }, []);

  useEffect(() => {
    api.get("/me/stats-overview").then(({ data }) => {
      setStats({ matches: data?.matches ?? 0, friends: data?.friends ?? 0, next_date: null });
      setNextMatch((data?.upcoming || [])[0] || null);
    }).catch(() => {});
  }, []);

  const nivel = user?.category || "Intermedio";
  const avatar3d = user?.avatar3d || {};
  const img = theme ? abs(theme.hero_image_url) : null;
  const firstName = (userName || user?.name || "").split(" ")[0] || "Jugador";

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#CCFF00]/20 bg-gradient-to-br from-[#0a1830] via-black to-[#0a1830]" data-testid="dashboard-hero-banner">
      {/* Background image (admin theme) */}
      {img && (
        <div className="absolute inset-0">
          <img src={img} alt={theme?.name} className="w-full h-full object-cover opacity-70" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-black/80" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
        </div>
      )}

      {/* Content */}
      <div className="relative p-4 md:p-8 min-h-[420px] md:min-h-[460px] flex flex-col justify-between">
        {/* Top: handwritten tagline + welcome card */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="font-['Caveat',cursive] text-3xl md:text-5xl text-white leading-tight drop-shadow-lg" style={{ fontFamily: '"Caveat", "Brush Script MT", cursive' }}>
              Tu deporte,<br />tu comunidad,<br />tu app<span className="text-[#CCFF00]">.</span>
            </p>
            <div className="h-0.5 w-16 bg-[#CCFF00] mt-2" />
          </div>

          {/* Welcome + Nivel + mini AR avatar */}
          <div className="w-full sm:w-[280px] md:w-[320px] rounded-xl border border-[#CCFF00]/40 bg-black/60 backdrop-blur-md p-3 md:p-4 shadow-[0_8px_32px_rgba(0,0,0,0.5)]" data-testid="hero-welcome-card">
            <div className="flex items-center gap-3">
              <HeroAvatar user={user} avatar3d={avatar3d} />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-widest text-gray-400">Bienvenido,</p>
                <p className="font-['Barlow_Condensed'] text-xl md:text-2xl font-black uppercase text-white leading-tight flex items-center gap-1 truncate">
                  {firstName} <Crown size={16} weight="fill" className="text-[#CCFF00] shrink-0" />
                </p>
                <p className="text-[10px] uppercase tracking-widest text-gray-400 mt-1">
                  Nivel <b className="text-white normal-case">{nivel}</b>
                </p>
                <div className="h-1 bg-white/10 rounded-full overflow-hidden mt-1">
                  <div className="h-full bg-gradient-to-r from-[#CCFF00] to-emerald-400" style={{ width: "65%" }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right-side stats stack (floating on top of hero) */}
        <div className="absolute right-4 md:right-8 top-[110px] md:top-[150px] w-[260px] md:w-[280px] rounded-xl border border-white/10 bg-black/60 backdrop-blur-md p-3 hidden sm:block" data-testid="hero-stats-card">
          <StatRow icon={Trophy} label="Partidos" value={stats.matches} />
          <StatRow icon={UsersThree} label="Amigos" value={stats.friends} />
          <StatRow
            icon={CalendarCheck}
            label="Próxima cita"
            value={nextMatch ? `${nextMatch.day_short} ${nextMatch.day_num} ${nextMatch.month_short}` : "—"}
            linkTo="/dashboard/retas"
            last
          />
        </div>

        {/* Bottom row: CTA + Aerotomas tile */}
        <div className="flex items-end justify-between gap-3 flex-wrap mt-6 relative z-[1]">
          <Link
            to="/dashboard/retas"
            className="group flex-1 sm:flex-none min-w-[220px] md:min-w-[280px] rounded-xl border border-cyan-400/40 bg-black/50 backdrop-blur-md px-4 py-3 flex items-center gap-3 hover:border-cyan-400 transition-all"
            data-testid="hero-cta-reta"
          >
            <Target size={28} weight="duotone" className="text-cyan-300" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-gray-400">Tu próxima</p>
              <p className="font-['Barlow_Condensed'] text-xl font-black uppercase text-white tracking-tight">reta te espera</p>
            </div>
            <span className="shrink-0 w-8 h-8 rounded-full bg-[#CCFF00] text-black flex items-center justify-center font-black group-hover:translate-x-1 transition-transform">›</span>
          </Link>

          <Link to="/dashboard/admin/dashboard-theme" className="group hidden md:flex items-center gap-2 rounded-xl border border-orange-400/40 bg-gradient-to-br from-orange-500/80 to-red-700/70 backdrop-blur-md px-3 py-2.5 hover:scale-[1.02] transition-transform">
            <Drone size={28} weight="duotone" className="text-white" />
            <div className="min-w-0">
              <p className="font-['Barlow_Condensed'] text-sm font-black uppercase text-white tracking-tight leading-tight">Aerotomas<br />Controladas</p>
              <p className="text-[9px] text-white/90">Drones con paisaje</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatRow({ icon: Icon, label, value, last = false, linkTo }) {
  const content = (
    <div className={`flex items-center gap-2 py-1.5 ${last ? "" : "border-b border-white/10"}`}>
      <Icon size={16} weight="duotone" className="text-[#CCFF00] shrink-0" />
      <span className="text-xs text-gray-300 flex-1">{label}</span>
      <span className="font-['Barlow_Condensed'] text-xl font-black text-white leading-none">{value}</span>
      {linkTo && <span className="text-[#CCFF00] text-sm leading-none">›</span>}
    </div>
  );
  return linkTo ? <Link to={linkTo} className="block">{content}</Link> : content;
}

// Mini 3D humanoid in the welcome card — rotating close-up to the head so
// the user's uploaded selfie texture is clearly visible. Clicking the circle
// jumps to Avatar Studio to tweak the humanoid.
function HeroAvatar({ user, avatar3d }) {
  const facePhoto = avatar3d?.facePhoto || user?.profile_photo_url || user?.photo_path || null;
  return (
    <Link
      to="/dashboard"
      className="relative shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden border-2 border-[#CCFF00]/60 bg-black group ring-2 ring-[#CCFF00]/10 hover:ring-[#CCFF00]/40 transition-all"
      data-testid="hero-mini-avatar"
      title="Edita tu avatar 3D AR"
    >
      <div className="absolute inset-0">
        <ThreeAvatar
          brandColors={{
            jersey: avatar3d.jersey || "#CCFF00",
            pants: avatar3d.pants || "#0A0A0A",
            shoes: avatar3d.shoes || "#E63946",
            pala: avatar3d.pala || "#00A0FF",
            skin: avatar3d.skin || "#E8C39E",
          }}
          outfit={{
            apparel: avatar3d.apparel || "short",
            shoes: avatar3d.shoesType || "low",
            headwear: avatar3d.headwear || "none",
          }}
          animation="idle"
          cameraMode="face"
          facePhotoUrl={facePhoto ? abs(facePhoto) : null}
          height={80}
        />
      </div>
      <span className="absolute bottom-0 left-0 right-0 bg-[#CCFF00]/95 text-black text-[7px] font-black uppercase tracking-widest text-center leading-[11px] pointer-events-none">AR 3D</span>
    </Link>
  );
}
